export * from '@sit/graphql';
import { axiosInstance, getToken, buildHeaders } from '@sit/openapi';
export * from '@sit/openapi';
import { CompanyIdHeader } from '@sit/core';
import { InMemoryCache } from '@apollo/client/cache/inmemory/inMemoryCache.js';
import { ApolloClient } from '@apollo/client/core/ApolloClient.js';
import { split } from '@apollo/client/link/core/split.js';
import { getMainDefinition } from '@apollo/client/utilities/index.js';
import { useMemo, useRef, useEffect, useState } from 'react';
import { setContext } from '@apollo/client/link/context/index.js';
import { ApolloLink } from '@apollo/client/link/core/ApolloLink.js';
import { onError } from '@apollo/client/link/error/index.js';
import { HttpLink } from '@apollo/client/link/http/HttpLink.js';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions/index.js';
import { createClient as createClient$1 } from 'graphql-ws';
import isEqual from 'lodash/isEqual.js';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import moment from 'moment';
import get from 'lodash/get.js';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

function isPromise(p) {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
    !!p && typeof p === 'object' && typeof p.then === 'function'
  );
}

let sessionStorage;
const parseCompany = val => {
  if (!val) return;
  const company = JSON.parse(val);
  if (company && typeof company === 'object') {
    const casted = company;
    if (typeof casted.id !== 'string') return; // id is required
    return {
      id: casted.id
    };
  }
};
const companyKey = 'currentCompany';
/**
 * Gets the current company that the user is viewing from session storage.
 * @returns
 */
function getCompanyFromSessionStorage() {
  const result = sessionStorage.getItem(companyKey);
  return isPromise(result) ? result.then(parseCompany) : parseCompany(result);
}
function removeCompanyFromSessionStorage() {
  return sessionStorage.removeItem(companyKey);
}
function setCompanyForSessionStorage(companyId) {
  void sessionStorage.setItem(companyKey, JSON.stringify({
    id: companyId
  }));
}
function initializeSessionStorage(storage) {
  sessionStorage = storage;
}

async function buildHeadersWithCompanyId(additionalHeaders, token) {
  const result = await getCompanyFromSessionStorage();
  const headers = result != null && result.id ? _extends({}, additionalHeaders, {
    [CompanyIdHeader]: result.id
  }) : additionalHeaders;
  return buildHeaders(headers, token);
}
async function interceptRequestSent(config) {
  const token = getToken();
  config.headers = await buildHeadersWithCompanyId(config.headers || {}, token);
  return config;
}
function initializeAxiosRequestInterceptor() {
  axiosInstance.interceptors.request.use(interceptRequestSent, error => Promise.reject(error));
}

const baseHttpLink = uri => new HttpLink({
  uri
});
const authLink = ({
  token
}) => setContext((_, {
  headers
}) => {
  if (!token) return {
    headers
  };
  return {
    headers: _extends({}, headers, {
      authorization: `JWT ${token}`
    })
  };
});
const logoutLink = onError(() => undefined);
const httpLink = ({
  token,
  url
}) => ApolloLink.from([authLink({
  token
}), logoutLink, baseHttpLink(url)]);

// the socket close timeout due to token expiry
// const tokenExpiryTimeout: number | null = null;
const wsLink = ({
  token,
  url
}) => new GraphQLWsLink(createClient$1({
  url,
  connectionParams: async () => {
    const currentToken = token;
    return {
      authToken: currentToken
    };
  },
  // TODO: Refresh auth token in WebSocket link
  on: {
    //   connected: (untypedSocket) => {
    //     const socket = untypedSocket as WebSocket; // Their docs state it's `WebSocket` but they can't type it due to DOM typings being unusable in Node.js.
    //     // clear timeout on every connect for debouncing the expiry
    //     clearTimeout(tokenExpiryTimeout);
    //     // set a token expiry timeout for closing the socket
    //     // with an `4403: Forbidden` close event indicating
    //     // that the token expired. the `closed` event listner below
    //     // will set the token refresh flag to true
    //     tokenExpiryTimeout = setTimeout(() => {
    //       if (socket.readyState === WebSocket.OPEN) socket.close(CloseCode.Forbidden, 'Forbidden');
    //     }, getCurrentTokenExpiresIn());
    //   },
    closed: event => {
    }
  }
}));

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = ({
  token,
  url,
  wsUrl
}) => split(({
  query
}) => {
  const definition = getMainDefinition(query);
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    definition.kind === 'OperationDefinition' &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    definition.operation === 'subscription'
  );
}, wsLink({
  token,
  url: wsUrl
}), httpLink({
  token,
  url
}));
function createHttpClient({
  token,
  url
}) {
  return new ApolloClient({
    link: httpLink({
      token,
      url
    }),
    cache: new InMemoryCache()
  });
}
function createClient({
  token,
  url,
  wsUrl
}) {
  if (token) {
    return new ApolloClient({
      link: splitLink({
        token,
        url,
        wsUrl
      }),
      cache: new InMemoryCache()
    });
  }
  return createHttpClient({
    token,
    url
  });
}
function useAuthApolloClient(token, url, wsUrl, skip = false) {
  const apolloInstance = useMemo(() => {
    if (skip) return undefined;
    return createClient({
      token,
      url,
      wsUrl
    });
  }, [skip, token, wsUrl, url]);
  return apolloInstance;
}

/**
 * A hook that can be used to simulation the componentDidUpdate class component lifecycle method.
 */
const useComponentDidUpdate = (callback, ...state) => {
  const isMounted = useRef(false);
  const previousState = useRef(state);
  useEffect(() => {
    if (isMounted.current === false) {
      isMounted.current = true;
    } else {
      callback(...previousState.current);
    }
    previousState.current = state;
  }, state);
};

/**
 * Only usable for DOM elements
 * @see https://usehooks.com/useHover/
 */
function useHover(ref) {
  const [value, setValue] = useState(false);
  useEffect(() => {
    const handleMouseOver = () => setValue(true);
    const handleMouseOut = () => setValue(false);
    const node = ref.current;
    if (node) {
      node.addEventListener('mouseover', handleMouseOver);
      node.addEventListener('mouseout', handleMouseOut);
      return () => {
        node.removeEventListener('mouseover', handleMouseOver);
        node.removeEventListener('mouseout', handleMouseOut);
      };
    }
  }, [ref.current]);
  return value;
}

// Hook that stores the previous value of a prop
function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

function formatIntacctEntityName(entity, fallback = 'Top level') {
  if (!entity) {
    return fallback;
  }
  const {
    intacctEntityId,
    name
  } = entity;
  const isTopLevelEntity = !intacctEntityId;
  return isTopLevelEntity ? fallback : `${intacctEntityId}--${name}`;
}

const LOGOUT_REQUEST = 'LOGOUT_REQUEST';
const LOGOUT_REQUEST_FAILURE = 'LOGOUT_REQUEST_FAILURE';
const LOGOUT_REQUEST_SUCCESS = 'LOGOUT_REQUEST_SUCCESS';

/**
 * Create a "selector creator" that uses `lodash.isEqual` instead of `===`
 */
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

function canEditTimesheet(isStaff, userData) {
  return isStaff && userData.canEditStaffTimesheets || !isStaff && userData.canEditTimesheets;
}

const getDuration = seconds => {
  if (!seconds) {
    return '0.00';
  }
  return moment.duration(seconds, 'seconds').asHours().toFixed(2);
};
const getSecondsFromHours = hours => {
  if (typeof hours !== 'number') {
    return 0;
  }
  return moment.duration(hours.toFixed(2), 'hours').asSeconds();
};
const getHoursFromSeconds = seconds => {
  if (seconds === null) {
    return 0.0;
  }
  return Number.parseFloat(moment.duration(seconds, 'seconds').asHours().toFixed(2));
};
const formatHours = hours => {
  if (hours === null) {
    return '0.00';
  }
  if (typeof hours === 'string') {
    return Number.parseFloat(hours).toFixed(2);
  }
  return hours.toFixed(2);
};

// sort the entities by intacctEntityId. This is the same as the order they appear in the dropdown in the Intacct UI.
// if intacctEntityId is falsy, it will be sorted to the front of the list
const userEntityComparator = (a, b) => {
  var _a$intacctEntityId, _b$intacctEntityId;
  return ((_a$intacctEntityId = a.intacctEntityId) != null ? _a$intacctEntityId : '').localeCompare((_b$intacctEntityId = b.intacctEntityId) != null ? _b$intacctEntityId : '');
};

function getProgress(data) {
  if (typeof data === 'number') return data;
  if (data == null || typeof data !== 'object' || !('progress' in data)) return 0;
  return typeof data.progress === 'number' ? data.progress :
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  get(data, 'progress.progress');
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation> -- any is correct.
const loadingChunkRetry = (fn, retriesLeft = 5, interval = 1000) => {
  return new Promise((resolve, reject) => {
    fn().then(resolve).catch(error => {
      setTimeout(() => {
        if (retriesLeft === 1) {
          // reject('maximum retries exceeded')
          reject(error);
          return;
        }
        // Passing on 'reject' is the important part
        loadingChunkRetry(fn, retriesLeft - 1, interval * (6 - retriesLeft)).then(resolve, reject);
      }, interval);
    });
  });
};

// It's not feasible to type these without `any` unless we make these functions hard to use.
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const sortByDateString = (dataToSort, propertyToSortBy, order) => {
  const sortedData = dataToSort.sort((a, b) => {
    var _a$propertyToSortBy, _b$propertyToSortBy;
    const formatedAValue = (_a$propertyToSortBy = a[propertyToSortBy]) == null ? void 0 : _a$propertyToSortBy.replace(' +', '+');
    const formatedBValue = (_b$propertyToSortBy = b[propertyToSortBy]) == null ? void 0 : _b$propertyToSortBy.replace(' +', '+');
    if (order === 'ascending') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- was here before, don't want to change
      return new Date(formatedAValue) - new Date(formatedBValue);
    }
    if (order === 'descending') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- was here before, don't want to change
      return new Date(formatedBValue) - new Date(formatedAValue);
    }
    return 0;
  });
  return sortedData;
};
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const sortByDate = (dataToSort, propertyToSortBy, order) => {
  const sortedData = dataToSort.sort((a, b) => {
    const formatedAValue = a[propertyToSortBy];
    const formatedBValue = b[propertyToSortBy];
    if (order === 'ascending') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- was here before, don't want to change
      return formatedAValue - formatedBValue;
    }
    if (order === 'descending') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- was here before, don't want to change
      return formatedBValue - formatedAValue;
    }
    return 0;
  });
  return sortedData;
};
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const sortByNumber = (dataToSort, propertyToSortBy, order) => {
  const sortedData = dataToSort.sort((a, b) => {
    const aProp = a[propertyToSortBy];
    const bProp = b[propertyToSortBy];
    const parsedValueA = typeof aProp === 'string' ? Number.parseInt(aProp) : aProp;
    const parsedValueB = typeof bProp === 'string' ? Number.parseInt(bProp) : bProp;
    if (order === 'ascending') return parsedValueA - parsedValueB;
    if (order === 'descending') return parsedValueB - parsedValueA;
    return 0;
  });
  return sortedData;
};
const sortByBoolean = (dataToSort, propertyToSortBy, order) => {
  const sortedData = dataToSort.sort((a, b) => {
    const aProp = a[propertyToSortBy];
    const bProp = b[propertyToSortBy];
    if (aProp === bProp) return 0;
    if (aProp) return order === 'ascending' ? -1 : 1;
    return order === 'ascending' ? 1 : -1;
  });
  return sortedData;
};
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const sortByString = (dataToSort, propertyToSortBy, order) => {
  const fn = typeof propertyToSortBy === 'function' ? propertyToSortBy : a => {
    var _a$propertyToSortBy2;
    return ((_a$propertyToSortBy2 = a[propertyToSortBy]) == null ? void 0 : _a$propertyToSortBy2.toUpperCase()) || '';
  };
  const sortedData = dataToSort.sort((a, b) => {
    const stringA = fn(a);
    const stringB = fn(b);
    if (order === 'ascending') return stringA.localeCompare(stringB);
    if (order === 'descending') return stringB.localeCompare(stringA);
    return 0;
  });
  return sortedData;
};

export { LOGOUT_REQUEST, LOGOUT_REQUEST_FAILURE, LOGOUT_REQUEST_SUCCESS, canEditTimesheet, createClient, createDeepEqualSelector, createHttpClient, formatHours, formatIntacctEntityName, getCompanyFromSessionStorage, getDuration, getHoursFromSeconds, getProgress, getSecondsFromHours, initializeAxiosRequestInterceptor, initializeSessionStorage, loadingChunkRetry, removeCompanyFromSessionStorage, sessionStorage, setCompanyForSessionStorage, sortByBoolean, sortByDate, sortByDateString, sortByNumber, sortByString, useAuthApolloClient, useComponentDidUpdate, useHover, usePrevious, userEntityComparator };
//# sourceMappingURL=index.modern.js.map
